/**
 * Anteprima degli allegati prima dell'invio: uno o piu' file, didascalia modificabile
 * per ciascuno, togli file, aggiungine altri. Invio in fila con l'invio esistente
 * (useWhatsAppSend().sendMedia): un file che fallisce resta in chat con "Riprova".
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Send, FileText, Film, Loader2, AlertCircle } from 'lucide-react';
import { tip, iconTip } from '@/components/messages/ChatTooltip';
import { useAttachmentStore } from './attachmentStore';
import { attachmentKind, validateAttachment, formatBytes, filesFromList } from './attachments';

interface AttachmentPreviewProps {
  chatId: string;
  chatName?: string;
  onSend: (file: File, kind: 'image' | 'video' | 'document', caption: string) => Promise<void>;
}

const NO_ITEMS: never[] = [];

function useObjectUrl(file: File | null): string | null {
  const url = useMemo(() => (file && file.type.startsWith('image/') ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  return url;
}

export function AttachmentPreview({ chatId, chatName, onSend }: AttachmentPreviewProps) {
  const items = useAttachmentStore((s) => (s.chatId === chatId ? s.items : NO_ITEMS));
  const { add, setCaption, remove, clear } = useAttachmentStore.getState();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  const active = items.find((i) => i.id === activeId) ?? items[0] ?? null;
  const previewUrl = useObjectUrl(active?.file ?? null);
  const errors = useMemo(() => new Map(items.map((i) => [i.id, validateAttachment(i.file)])), [items]);
  const sendable = items.filter((i) => !errors.get(i.id));

  useEffect(() => { captionRef.current?.focus(); }, [active?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !progress) { e.preventDefault(); clear(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [clear, progress]);

  if (items.length === 0) return null;

  const handleSend = async () => {
    if (sendable.length === 0 || progress) return;
    setProgress({ done: 0, total: sendable.length });
    for (let i = 0; i < sendable.length; i++) {
      const it = sendable[i];
      const kind = attachmentKind(it.file);
      await onSend(it.file, kind === 'audio' ? 'document' : kind, it.caption.trim());
      setProgress({ done: i + 1, total: sendable.length });
    }
    setProgress(null);
    clear();
  };

  const activeError = active ? errors.get(active.id) : null;
  const kind = active ? attachmentKind(active.file) : 'document';

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Anteprima allegati">
      <div className="absolute inset-0 bg-black/50" onClick={() => !progress && clear()} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-[rgba(55,53,47,0.09)] dark:border-white/10 flex flex-col max-h-[90vh]">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <h2 className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white truncate">
            {items.length === 1 ? 'Invia 1 file' : `Invia ${items.length} file`}{chatName ? ` a ${chatName}` : ''}
          </h2>
          <button type="button" onClick={clear} disabled={!!progress} className="p-1.5 rounded-lg hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 disabled:opacity-40" {...iconTip('Annulla, non inviare')}>
            <X className="w-4 h-4 text-[#787774]" />
          </button>
        </div>

        {active && (
          <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
            <div className="flex items-center justify-center rounded-lg bg-[rgba(55,53,47,0.04)] dark:bg-white/5 min-h-[180px] max-h-[40vh] overflow-hidden">
              {previewUrl && kind === 'image' ? (
                <img src={previewUrl} alt={active.file.name} className="max-h-[40vh] max-w-full object-contain" />
              ) : (
                <div className="flex flex-col items-center gap-2 p-6 text-center">
                  {kind === 'video' ? <Film className="w-10 h-10 text-purple-500" /> : <FileText className="w-10 h-10 text-orange-500" />}
                  <p className="text-sm font-medium text-[#37352F] dark:text-white break-all">{active.file.name}</p>
                  <p className="text-xs text-[#787774]">{formatBytes(active.file.size)}{kind === 'document' && active.file.type.startsWith('image/') ? ' (inviata come documento)' : ''}</p>
                </div>
              )}
            </div>
            {activeError ? (
              <p className="flex items-start gap-1.5 text-xs text-red-600"><AlertCircle className="w-4 h-4 flex-shrink-0" />{activeError}. Toglilo per inviare gli altri.</p>
            ) : (
              <textarea
                ref={captionRef}
                value={active.caption}
                onChange={(e) => setCaption(active.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Aggiungi una didascalia (facoltativa)"
                rows={2}
                disabled={!!progress}
                className="w-full text-sm text-[#37352F] dark:text-white bg-white dark:bg-gray-800 border border-[rgba(55,53,47,0.12)] dark:border-white/10 rounded-lg px-3 py-2 outline-none focus:border-[#2383E2] resize-none"
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-2 px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <div className="flex-1 flex items-center gap-1.5 overflow-x-auto">
            {items.map((it) => (
              <div key={it.id} className="relative flex-shrink-0 group/att">
                <button type="button" onClick={() => setActiveId(it.id)}
                  className={`w-12 h-12 rounded-md border text-[9px] leading-tight p-1 overflow-hidden text-[#787774] ${it.id === active?.id ? 'border-[#2383E2] ring-1 ring-[#2383E2]' : 'border-[rgba(55,53,47,0.12)] dark:border-white/10'} ${errors.get(it.id) ? 'bg-red-50 dark:bg-red-900/20' : ''}`}
                  {...tip(it.file.name)}>
                  <span className="line-clamp-3 break-all">{it.file.name}</span>
                </button>
                <button type="button" onClick={() => remove(it.id)} disabled={!!progress}
                  className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#37352F] text-white flex items-center justify-center opacity-0 group-hover/att:opacity-100 focus:opacity-100"
                  {...iconTip('Togli questo file')}>
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => addInputRef.current?.click()} disabled={!!progress}
              className="w-12 h-12 flex-shrink-0 rounded-md border border-dashed border-[rgba(55,53,47,0.2)] dark:border-white/20 flex items-center justify-center text-[#787774] hover:bg-[rgba(55,53,47,0.04)]"
              {...iconTip('Aggiungi altri file')}>
              <Plus className="w-4 h-4" />
            </button>
            <input ref={addInputRef} type="file" multiple className="hidden"
              onChange={(e) => { add(chatId, filesFromList(e.target.files)); e.target.value = ''; }} />
          </div>
          <button type="button" onClick={handleSend} disabled={sendable.length === 0 || !!progress}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#25D366] hover:bg-[#1eb957] disabled:opacity-50 rounded-lg">
            {progress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {progress ? `Invio ${Math.min(progress.done + 1, progress.total)} di ${progress.total}...` : 'Invia'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
